import React, { useEffect, useState, useRef, useCallback } from 'react';
import { isEmpty, isEqual, some, trim, lowerCase } from 'lodash';
import { messages } from '../../i18n';
import { FormInput } from '../../components/ui/form-input';
import {
  FormLabel,
  FormGroup,
  StyledUserInputDialog,
  ErrorMessage,
} from './edit-discovery-dialog.styles';

const MIN_NAME_LENGTH = 1;
const MAX_NAME_LENGTH = 100;

const detailTypes = {
  NAME: 'name',
  DESCRIPTION: 'description',
};

interface IProps {
  show?: string;
  detail?: string;
  value?: string;
  onOk?: any;
  onCancel?: any;
  existingDisplayNames?: string[]; // New prop for validation
}

export const EditDiscoveryDialog = ({
  value,
  detail,
  onOk,
  onCancel,
  existingDisplayNames = [],
}: IProps) => {
  const [currentValue, setCurrentValue] = useState(value);
  const [autoSelectInput, setAutoSelectInput] = useState(true);
  const inputRef = useRef(null);
  const initialValue = value;
  const otherDisplayNames = existingDisplayNames.filter(
    name => !isEqual(trim(lowerCase(name)), trim(lowerCase(value ?? ''))),
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (inputRef.current && autoSelectInput) {
      inputRef.current.focus();
      setAutoSelectInput(false);
    }
  }, [autoSelectInput]);

  const validate = useCallback(
    (val: string) => {
      if (trim(val)?.length < MIN_NAME_LENGTH) {
        return messages.formatString(
          messages.account.atLeastXChars,
          MIN_NAME_LENGTH,
        );
      }
      if (trim(val ?? '')?.length < MIN_NAME_LENGTH) {
        return messages.formatString(
          messages.account.atLeastXChars,
          MIN_NAME_LENGTH,
        );
      }
      if (trim(val ?? '')?.length > MAX_NAME_LENGTH) {
        return messages.formatString(
          messages.validation.maxLength,
          MAX_NAME_LENGTH,
        );
      }
      if (
        some(otherDisplayNames, name =>
          isEqual(trim(lowerCase(name)), trim(lowerCase(val))),
        )
      ) {
        return messages.vizSaveDialog.errorAlreadyInUse;
      }
      return null;
    },
    [otherDisplayNames],
  );

  useEffect(() => {
    if (isEqual(detail, detailTypes.NAME)) {
      setError(validate(currentValue || ''));
    }
  }, [currentValue, detail, validate]);

  const onChange = e => {
    const { value } = e.target;
    setCurrentValue(value);
    if (detail === detailTypes.NAME) {
      setError(validate(value));
    }
  };

  const onSave = useCallback(() => {
    onOk(currentValue);
  }, [onOk, currentValue]);

  const isSaveDisabled = useCallback(() => {
    if (isEqual(detail, detailTypes.NAME)) {
      return (
        !!error || isEmpty(currentValue) || isEqual(initialValue, currentValue)
      );
    }
    const detailTypesHandlers = {
      [detailTypes.DESCRIPTION]: isEqual(initialValue, currentValue),
    };
    if (detailTypesHandlers[detail]) {
      return detailTypesHandlers[detail];
    }
    return false;
  }, [detail, currentValue, initialValue, error]);

  useEffect(() => {
    const onEnter = e => {
      if (e.keyCode === 13) {
        e.preventDefault();
        if (!isSaveDisabled()) {
          onSave();
        }
      }
    };
    window.addEventListener('keypress', onEnter);
    return () => {
      window.removeEventListener('keypress', onEnter);
    };
  }, [isSaveDisabled, onSave]);

  return (
    <StyledUserInputDialog
      dialogClassName={'edit-discovery-dialog'}
      title={
        detail === detailTypes.NAME
          ? messages.library.rename
          : messages.library.editDescription
      }
      onCancel={onCancel}
      onOk={onSave}
      okButtonLabel={
        detail === detailTypes.NAME
          ? messages.library.rename
          : messages.library.update
      }
      okDisabled={isSaveDisabled()}
    >
      {detail === detailTypes.NAME && (
        <FormGroup>
          <FormLabel>{messages.name}</FormLabel>
          <FormInput
            value={currentValue}
            onChange={onChange}
            inputRef={inputRef}
            error={!!error}
          />
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </FormGroup>
      )}
      {detail === detailTypes.DESCRIPTION && (
        <FormGroup>
          <FormLabel>{messages.description}</FormLabel>
          <FormInput
            multiline
            value={currentValue}
            onChange={onChange}
            inputRef={inputRef}
          />
        </FormGroup>
      )}
    </StyledUserInputDialog>
  );
};
